import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import { generatePdiBooklet } from "./PdiPage";

function fmt(min) {
  if (min == null) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}
function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 16).replace("T", " ");
}

const STATUS_META = {
  completed:   { label: "Completed",   bg: "#dcfce7", color: "#15803d" },
  in_progress: { label: "In Progress", bg: "#dbeafe", color: "#1d4ed8" },
};

const SORT_COLS = [
  { id: "started_at",       label: "Date Started" },
  { id: "completed_at",     label: "Date Completed" },
  { id: "customer",         label: "Customer" },
  { id: "rowo",             label: "RO/WO" },
  { id: "template",         label: "Template" },
  { id: "duration_minutes", label: "Duration" },
  { id: "steps_answered",   label: "Steps" },
  { id: "total_defs",       label: "Deficiencies" },
];

export default function PdiManagePage() {
  const nav = useNavigate();

  // data
  const [sessions,   setSessions]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [templates,  setTemplates]  = useState([]);
  const [techList,   setTechList]   = useState([]);
  const [busy,       setBusy]       = useState(false);
  const [msg,        setMsg]        = useState("");

  // filters
  const [search,     setSearch]     = useState("");
  const [statusF,    setStatusF]    = useState("completed");
  const [templateF,  setTemplateF]  = useState("");
  const [techF,      setTechF]      = useState("");
  const [fromDate,   setFromDate]   = useState("");
  const [toDate,     setToDate]     = useState("");

  // sort
  const [sortCol,  setSortCol]  = useState("started_at");
  const [sortDir,  setSortDir]  = useState("desc");

  // pagination
  const PAGE = 100;
  const [offset, setOffset] = useState(0);

  // delete modal
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteBusy,     setDeleteBusy]     = useState(false);
  const [deleteError,    setDeleteError]    = useState("");
  const pwRef = useRef(null);

  const load = useCallback(async (off = 0) => {
    setBusy(true);
    setMsg("");
    try {
      const params = { limit: PAGE, offset: off };
      if (statusF)   params.status   = statusF;
      if (templateF) params.template = templateF;
      if (techF)     params.tech     = techF;
      if (fromDate)  params.from     = fromDate;
      if (toDate)    params.to       = toDate;
      if (search.trim()) params.q    = search.trim();

      const { data } = await api.get("/pdi/sessions", { params });
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      if (data.templates?.length) setTemplates(data.templates);
      if (data.techs?.length)     setTechList(data.techs);
      setOffset(off);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load sessions");
    } finally {
      setBusy(false);
    }
  }, [statusF, templateF, techF, fromDate, toDate, search]);

  useEffect(() => { load(0); }, [load]);

  // client-side sort
  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...sessions].sort((a, b) => {
      const va = a[sortCol] ?? "";
      const vb = b[sortCol] ?? "";
      if (va === "" && vb !== "") return 1;
      if (vb === "" && va !== "") return -1;
      if (typeof va === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [sessions, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const openDelete = (s) => {
    setDeleteTarget(s);
    setDeletePassword("");
    setDeleteError("");
    setTimeout(() => pwRef.current?.focus(), 80);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !deletePassword) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await api.delete(`/pdi/sessions/${deleteTarget.id}`, { data: { password: deletePassword } });
      setDeleteTarget(null);
      load(offset);
    } catch (e) {
      setDeleteError(e?.response?.data?.error || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  const SortTh = ({ col, children }) => (
    <th
      onClick={() => toggleSort(col)}
      style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}
    >
      {children}
      {sortCol === col && <span style={{ marginLeft: 4, opacity: 0.6 }}>{sortDir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );

  return (
    <div className="container">
      {/* Header */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>PDI Sessions</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {busy ? "Loading…" : `${total} sessions`}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={() => nav("/pdi/reports")}>Reports</button>
          <button className="btn-outline" onClick={() => load(offset)} disabled={busy}>Refresh</button>
        </div>
      </div>

      {msg && <div style={{ color: "#dc2626", marginBottom: 12, fontWeight: 700 }}>{msg}</div>}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          {/* Search */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">SEARCH</div>
            <input
              className="input"
              placeholder="RO/WO, customer, VIN…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && load(0)}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </div>

          {/* Status */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">STATUS</div>
            <select className="input" value={statusF} onChange={e => setStatusF(e.target.value)} style={{ width: "100%" }}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>

          {/* Template */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">TEMPLATE</div>
            <select className="input" value={templateF} onChange={e => setTemplateF(e.target.value)} style={{ width: "100%" }}>
              <option value="">All templates</option>
              {templates.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Tech */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">TECHNICIAN</div>
            <select className="input" value={techF} onChange={e => setTechF(e.target.value)} style={{ width: "100%" }}>
              <option value="">All techs</option>
              {techList.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* From */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">FROM</div>
            <input type="date" className="input" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>

          {/* To */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="muted">TO</div>
            <input type="date" className="input" value={toDate} onChange={e => setToDate(e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>

        {/* Filter actions */}
        <div className="row" style={{ gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
          <button className="btn-outline" style={{ fontSize: 13 }} onClick={() => {
            setSearch(""); setStatusF("completed"); setTemplateF(""); setTechF(""); setFromDate(""); setToDate("");
          }}>Clear</button>
          <button className="btn" style={{ fontSize: 13 }} onClick={() => load(0)} disabled={busy}>Apply</button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ borderBottom: "2px solid var(--card-border)" }}>
            <tr>
              <SortTh col="rowo">Unit</SortTh>
              <SortTh col="customer">Customer</SortTh>
              <SortTh col="template">Template</SortTh>
              <th style={{ padding: "10px 14px", fontWeight: 700, whiteSpace: "nowrap" }}>Tech</th>
              <SortTh col="started_at">Started</SortTh>
              <SortTh col="completed_at">Completed</SortTh>
              <SortTh col="duration_minutes">Duration</SortTh>
              <SortTh col="steps_answered">Steps</SortTh>
              <SortTh col="total_defs">Defs</SortTh>
              <th style={{ padding: "10px 14px", fontWeight: 700 }}>Status</th>
              <th style={{ padding: "10px 14px", fontWeight: 700 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(s => {
              const meta = STATUS_META[s.status] || {};
              const unitLabel = s.rowo ? `RO/WO ${s.rowo}` : s.stock_po ? `Stock ${s.stock_po}` : `#${s.inventory_id}`;
              const techLabel = s.techs || s.tech_initials || "—";
              const hasDefs   = s.total_defs > 0;
              return (
                <tr key={s.id} style={{ borderBottom: "1px solid var(--card-border)" }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ fontWeight: 700 }}>{unitLabel}</div>
                    {(s.body_make_model || s.type) && (
                      <div className="muted" style={{ fontSize: 11 }}>{s.body_make_model || s.type}</div>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div>{s.customer || "—"}</div>
                    {s.unit_location && <div className="muted" style={{ fontSize: 11 }}>{s.unit_location}</div>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{s.template}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div>{techLabel}</div>
                    {s.engineering_initials && (
                      <div className="muted" style={{ fontSize: 11 }}>Eng: {s.engineering_initials}</div>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{fmtDate(s.started_at)}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{fmtDate(s.completed_at)}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    {s.duration_minutes != null ? (
                      <span style={{ fontWeight: 700, color: s.duration_minutes > 120 ? "#dc2626" : s.duration_minutes > 60 ? "#d97706" : "#16a34a" }}>
                        {fmt(s.duration_minutes)}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>{s.steps_answered}</td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }}>
                    {hasDefs ? (
                      <span style={{ fontWeight: 700, color: s.open_defs > 0 ? "#dc2626" : "#16a34a" }}>
                        {s.total_defs}{s.open_defs > 0 ? ` (${s.open_defs} open)` : " ✓"}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ background: meta.bg, color: meta.color, borderRadius: 99, padding: "2px 10px", fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                      {meta.label || s.status}
                    </span>
                    {s.signature_path && (
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>✓ Signed</div>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
                      <button
                        className="btn-outline"
                        style={{ fontSize: 12, padding: "4px 10px", whiteSpace: "nowrap" }}
                        onClick={() => nav(`/pdi/${s.inventory_id}?template=${encodeURIComponent(s.template)}&table=all_inventory`)}
                      >
                        Open
                      </button>
                      <button
                        className="btn-outline"
                        style={{ fontSize: 12, padding: "4px 10px", whiteSpace: "nowrap" }}
                        onClick={() => generatePdiBooklet(s.id, api.get.bind(api))}
                        title="Print PDI booklet"
                      >
                        Print
                      </button>
                      <button
                        className="btn-outline"
                        style={{ fontSize: 12, padding: "4px 10px", whiteSpace: "nowrap", color: "#dc2626", borderColor: "#dc2626" }}
                        onClick={() => openDelete(s)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!busy && sorted.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 32, textAlign: "center" }} className="muted">
                  No sessions match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div className="row" style={{ justifyContent: "center", gap: 8, marginTop: 12 }}>
          <button className="btn-outline" onClick={() => load(Math.max(0, offset - PAGE))} disabled={offset === 0 || busy}>← Prev</button>
          <span className="muted" style={{ alignSelf: "center", fontSize: 13 }}>
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <button className="btn-outline" onClick={() => load(offset + PAGE)} disabled={offset + PAGE >= total || busy}>Next →</button>
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="card" style={{ maxWidth: 420, width: "100%" }}>
            <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 4, color: "#dc2626" }}>Delete PDI Session</div>
            <div className="muted" style={{ marginBottom: 16, fontSize: 14 }}>
              Permanently deletes session <strong>#{deleteTarget.id}</strong> —{" "}
              <strong>{deleteTarget.rowo ? `RO/WO ${deleteTarget.rowo}` : deleteTarget.customer || `Unit #${deleteTarget.inventory_id}`}</strong>.
              All step responses and deficiencies will be removed. This cannot be undone.
            </div>
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>Enter your password to confirm</div>
            <input
              ref={pwRef}
              className="input"
              type="password"
              placeholder="Your password…"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && confirmDelete()}
              style={{ width: "100%", marginBottom: 10, boxSizing: "border-box" }}
            />
            {deleteError && (
              <div style={{ color: "#dc2626", fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{deleteError}</div>
            )}
            <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
              <button className="btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</button>
              <button
                className="btn"
                style={{ background: "#dc2626" }}
                onClick={confirmDelete}
                disabled={deleteBusy || !deletePassword}
              >
                {deleteBusy ? "Deleting…" : "Delete Session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
